import { useEffect, useState } from "react";
import { api, apiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { Folder, FolderPlus, FileText, Download, ChevronRight, Search, FolderInput, Calendar, Trash2 } from "lucide-react";

// Shared folder browser for client documents.
//   base: GET endpoint (staff: /clients/{wid}/documents · portal: /portal/documents)
//   manageBase: staff manage prefix (/clients/{wid}) or null for read-only portal
//   download(f): fn to download a file
export function DocumentBrowser({ base, manageBase, download, accent = "#0f766e", testid = "document-browser" }) {
  const [folder, setFolder] = useState(null);
  const [q, setQ] = useState("");
  const [data, setData] = useState(null);
  const [newFolder, setNewFolder] = useState("");
  const [creating, setCreating] = useState(false);
  const canManage = !!manageBase;

  const load = () => api.get(`${base}?folder=${encodeURIComponent(folder || "")}&q=${encodeURIComponent(q.trim())}`)
    .then((r) => setData(r.data)).catch((e) => toast.error(apiError(e)));
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { load(); }, [base, folder]);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { const t = setTimeout(load, 300); return () => clearTimeout(t); }, [q]);

  const createFolder = async () => {
    const name = newFolder.trim(); if (!name) return;
    setCreating(true);
    try { await api.post(`${manageBase}/folders`, { name }); setNewFolder(""); toast.success(`Folder "${name}" created`); setFolder(null); load(); }
    catch (e) { toast.error(apiError(e)); } finally { setCreating(false); }
  };
  const move = async (fileId, folderId) => {
    try { await api.put(`${manageBase}/documents/${fileId}/move`, { folder_id: folderId || null }); toast.success("Moved"); load(); }
    catch (e) { toast.error(apiError(e)); }
  };
  const removeFolder = async (fid) => {
    if (!window.confirm("Delete this folder? Files inside move back to date grouping.")) return;
    try { await api.delete(`${manageBase}/folders/${fid}`); toast.success("Folder deleted"); setFolder(null); load(); }
    catch (e) { toast.error(apiError(e)); }
  };

  if (!data) return <p className="text-sm text-slate-400">Loading documents…</p>;
  const namedFolders = (data.folders || []).filter((f) => f.kind === "folder");

  return (
    <div className="space-y-4" data-testid={testid}>
      <div className="flex flex-wrap items-center gap-2">
        <div className="flex items-center gap-1 text-sm text-slate-500 flex-1 min-w-0">
          {data.breadcrumb?.map((b, i) => (
            <span key={b.id || "root"} className="flex items-center gap-1">
              {i > 0 && <ChevronRight className="w-3.5 h-3.5 text-slate-300" />}
              <button onClick={() => setFolder(b.id === "__search__" ? folder : b.id)} className={`truncate ${i === data.breadcrumb.length - 1 ? "text-slate-900 font-medium" : "hover:underline"}`} data-testid={`crumb-${b.id || "root"}`}>{b.name}</button>
            </span>
          ))}
        </div>
        <div className="relative">
          <Search className="w-4 h-4 text-slate-400 absolute left-3 top-1/2 -translate-y-1/2" />
          <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search documents…" className="rounded-full border pl-9 pr-3 py-1.5 text-sm bg-white w-48" style={{ borderColor: "var(--line, #e5e7eb)" }} data-testid="doc-search" />
        </div>
      </div>

      {canManage && !folder && !q.trim() && (
        <div className="flex items-center gap-2">
          <input value={newFolder} onChange={(e) => setNewFolder(e.target.value)} onKeyDown={(e) => e.key === "Enter" && createFolder()} placeholder="New folder name" className="rounded-full border px-3 py-1.5 text-sm bg-white" style={{ borderColor: "var(--line, #e5e7eb)" }} data-testid="new-folder-input" />
          <Button size="sm" variant="outline" className="rounded-full" onClick={createFolder} disabled={creating} data-testid="new-folder-btn"><FolderPlus className="w-4 h-4 mr-1" /> Create folder</Button>
        </div>
      )}

      {(data.folders?.length > 0) && (
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-2">
          {data.folders.map((f) => (
            <div key={f.id} className="group flex items-center gap-2 rounded-xl border p-3 hover:bg-slate-50 cursor-pointer" style={{ borderColor: "var(--line, #eef0f3)" }} onClick={() => setFolder(f.id)} data-testid={`folder-${f.id}`}>
              {f.kind === "date" ? <Calendar className="w-5 h-5" style={{ color: accent }} /> : <Folder className="w-5 h-5" style={{ color: accent }} />}
              <span className="flex-1 truncate text-sm font-medium text-slate-800">{f.name}</span>
              <span className="text-xs text-slate-400">{f.count}</span>
              {canManage && f.kind === "folder" && <button onClick={(e) => { e.stopPropagation(); removeFolder(f.id); }} className="text-slate-300 hover:text-red-500 opacity-0 group-hover:opacity-100" data-testid={`folder-del-${f.id}`}><Trash2 className="w-3.5 h-3.5" /></button>}
            </div>
          ))}
        </div>
      )}

      <div className="panel divide-y" style={{ borderColor: "var(--line, #eef0f3)" }}>
        {(data.files || []).map((f) => (
          <div key={f.id} className="p-3 flex items-center gap-2 text-sm hover:bg-slate-50" data-testid={`doc-${f.id}`}>
            <FileText className="w-4 h-4 text-slate-400 shrink-0" />
            <button onClick={() => download(f)} className="flex-1 min-w-0 text-left truncate text-slate-800 hover:underline" data-testid={`doc-open-${f.id}`}>{f.name}</button>
            {f.folder_name && <span className="chip border-slate-200 text-slate-500 text-[10px] hidden sm:inline">{f.folder_name}</span>}
            <span className="text-xs text-slate-400 hidden sm:inline">{f.created_at ? new Date(f.created_at).toLocaleDateString() : ""}</span>
            {canManage && !q.trim() && (
              <select value={f.folder_id || ""} onClick={(e) => e.stopPropagation()} onChange={(e) => move(f.id, e.target.value)} className="text-xs border rounded-md px-1.5 py-1 bg-white max-w-[9rem]" style={{ borderColor: "var(--line, #e5e7eb)" }} data-testid={`doc-move-${f.id}`} title="Move to folder">
                <option value="">No folder</option>
                {namedFolders.map((nf) => <option key={nf.id} value={nf.id}>{nf.name}</option>)}
              </select>
            )}
            <button onClick={() => download(f)} className="text-slate-400 hover:text-slate-700" data-testid={`doc-dl-${f.id}`}><Download className="w-4 h-4" /></button>
          </div>
        ))}
        {(data.files || []).length === 0 && (data.folders || []).length === 0 && <p className="p-6 text-sm text-slate-400 text-center">{q.trim() ? "No documents match your search." : "No documents here yet."}</p>}
      </div>
      {canManage && folder && !folder.startsWith?.("date:") && (data.files || []).length > 0 && <p className="text-[11px] text-slate-400 flex items-center gap-1"><FolderInput className="w-3 h-3" /> Use the dropdown on a file to move it between folders.</p>}
    </div>
  );
}
